import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { LegacyStyleVariants, List, ListItem, StyleVariants } from '@site-mate/dashpivot-shared-library';

import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { HttpClientService } from 'app/shared/service/http-client.service';

type ListParent = { hierarchy: Exclude<AppHierarchy, 'team'>; id: string };

interface CreateListItemOptions {
  position: number;
  name: string;
  parent: ListParent;
}

interface DeployListItemOptions {
  name: string;
  projectId: string;
}

@Injectable()
export class ListItemsService {
  private hierarchyMap = {
    [AppHierarchy.Organisation]: 'companies',
    [AppHierarchy.Project]: 'projects',
  };

  constructor(private readonly httpClientService: HttpClientService) {}

  create(listId: string, { position, name, parent }: CreateListItemOptions) {
    const url = `${this.hierarchyMap[parent.hierarchy]}/${parent.id}/lists/${listId}/rows`;

    return this.httpClientService.post<{ fullList: List; newListRow: any }>(url, {
      position,
      name,
    });
  }

  getListItems(listId: string, params: { [key: string]: string }) {
    return this.httpClientService.get<ListItem[]>(`lists/${listId}/items`, params);
  }

  deploy(listId: string, { name, projectId }: DeployListItemOptions) {
    const url = `projects/${projectId}/lists/${listId}/rows/deploy`;
    return this.httpClientService.post<{ fullList: List; deployedListRow: any }>(url, { name });
  }

  undeploy(listId: string, listItemIds: string[], projectId: string): Observable<List> {
    return this.httpClientService.post<List>(`lists/${listId}/undeploy`, { projectId, listItemIds });
  }

  removeListItems(
    listId: string,
    listItemIds: string,
    parentId: string,
    hierarchy: AppHierarchy,
  ): Observable<List> {
    const folderMapping = { [AppHierarchy.Organisation]: 'companies', [AppHierarchy.Project]: 'projects' };
    const folderLevel = folderMapping[hierarchy];

    return this.httpClientService.remove(`${folderLevel}/${parentId}/lists/${listId}/rows`, { listItemIds });
  }

  deployListViaCsv(listId: string, parentId: string, formData: FormData) {
    return this.httpClientService.putUpload(
      `v1/projects/${parentId}/lists/${listId}/deploy-csv-file`,
      formData,
    );
  }

  applyStyle({
    listId,
    parent,
    itemIds,
    style,
  }: {
    listId: string;
    parent: ListParent;
    itemIds: string[];
    style: StyleVariants | LegacyStyleVariants;
  }) {
    const url = `${this.hierarchyMap[parent.hierarchy]}/${parent.id}/lists/${listId}/rows`;

    return this.httpClientService.put<{ fullList: List; updatedRows: any[] }>(url, {
      itemIds,
      style,
    });
  }
}
